import { useState, useEffect } from 'react';
import { X, Smartphone } from 'lucide-react';
import { isNative } from '@/lib/capacitor';
import { isDeepLinksEnabled } from '@/services/systemSettings';

const BANNER_DISMISSED_KEY = 'smartAppBannerDismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Store URLs - Replace with actual store IDs when published
const APP_STORE_URL = 'https://apps.apple.com/app/idAPP_STORE_ID';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=de.parentpath.app';

interface SmartAppBannerProps {
  /** Force show banner (for testing) */
  forceShow?: boolean;
}

/**
 * Smart App Banner - shows a banner on mobile web to open the native app
 * - Only shows on mobile devices (not in native app)
 * - Can be dismissed (remembers for 7 days)
 * - Shows app icon and "Open in App" button
 */
export function SmartAppBanner({ forceShow = false }: SmartAppBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Don't show in native app
    if (isNative && !forceShow) return;

    // Check if on mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile && !forceShow) return;

    // Check if banner was recently dismissed
    const dismissedAt = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < DISMISS_DURATION_MS) {
        return;
      }
    }

    // Check if deeplinks are enabled in system settings
    const checkDeepLinksEnabled = async () => {
      const enabled = await isDeepLinksEnabled();
      if (!enabled && !forceShow) return;

      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1000);
    };

    checkDeepLinksEnabled();
  }, [forceShow]);

  const handleDismiss = () => {
    setIsClosing(true);
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleOpenApp = () => {
    const currentPath = window.location.pathname + window.location.search;

    // Try to open the app with the current path
    // This uses the custom URL scheme
    const appUrl = `parentpath:/${currentPath}`;

    // Detect platform for store fallback
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const storeUrl = isIOS ? APP_STORE_URL : PLAY_STORE_URL;

    // Create a hidden iframe to attempt opening the app
    // This prevents the browser from showing an error if the app isn't installed
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = appUrl;
    document.body.appendChild(iframe);

    // Fallback: If app doesn't open, redirect to app store after a delay
    setTimeout(() => {
      document.body.removeChild(iframe);
      // Redirect to app store if app isn't installed
      window.location.href = storeUrl;
    }, 1500);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[100] transition-transform duration-300 ${
        isClosing ? '-translate-y-full' : 'translate-y-0'
      }`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="bg-pp-dark/95 backdrop-blur-md border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          {/* App Icon */}
          <div className="w-12 h-12 bg-gradient-to-br from-pp-gradient-start to-pp-gradient-end rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm">ParentPath App</p>
            <p className="text-white/60 text-xs truncate">
              Bessere Erfahrung in der App
            </p>
          </div>

          {/* Open Button */}
          <button
            onClick={handleOpenApp}
            className="px-4 py-2 bg-pp-orange rounded-lg text-white text-sm font-medium hover:bg-pp-orange/90 transition-colors flex-shrink-0"
          >
            Öffnen
          </button>

          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="p-1 text-white/40 hover:text-white/70 transition-colors flex-shrink-0"
            aria-label="Banner schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
